<div id="head"></div>
<h1 class="sr-only">Color Palette Generator - Build Hex Palettes for Vibe Coding &amp; Design</h1>

<!-- ===================== Hero: the interactive palette builder ===================== -->
<div [attr.id]="TOOLS[ 0 ].key + '-' + TOOLS[ 0 ].products[ 1 ]?.key"
     class="default-section initial-section metallic-section no-buttons"
     [class.striped-bg]="!!heroBgValue"
     [class.cp-hero-noblur]="!!foundTheme || !!uploadedImg"
     [style.--hero-bg]="heroBgValue"
     data-section="top">
  <!-- the palette's own title and its source as plain text on top, as the color tool shows its colour's name (owner 2026-10-12) -->
  <div class="cp-hero-head" [ngSwitch]="subtitle.kind">
    <ng-container *ngSwitchCase="'theme'">
      <p class="tools-title" aria-hidden="true">Codigrate Theme Palette</p>
      <p class="tools-subtitle">Source: {{ subtitle.themeName }}</p>
    </ng-container>
    <ng-container *ngSwitchCase="'image'">
      <p class="tools-title" aria-hidden="true">Image Palette</p>
      <p class="tools-subtitle">Source: {{ subtitle.imageName || 'uploaded image' }}</p>
    </ng-container>
    <ng-container *ngSwitchCase="'harmony'">
      <p class="tools-title" aria-hidden="true">{{ schemeTitle }} Palette</p>
      <p class="tools-subtitle">Source: {{ subtitle.name }}</p>
    </ng-container>
    <ng-container *ngSwitchCase="'full'">
      <p class="tools-title" aria-hidden="true">Random Palette</p>
      <p class="tools-subtitle">Source: {{ subtitle.seed ? 'seed-' + subtitle.seed : 'Random' }}</p>
    </ng-container>
    <ng-container *ngSwitchDefault>
      <p class="tools-title" aria-hidden="true">Custom Theme</p>
      <p class="tools-subtitle">Source: {{ subtitle.seed ? 'seed-' + subtitle.seed : 'Custom' }}</p>
    </ng-container>
  </div>
  <app-palette-widget
    [studio]="true"
    [palette]="palette"
    [subtitle]="subtitle"
    [scheme]="currentGenerator"
    [count]="count"
    (countChange)="count = $event"
    (editColor)="onEditColor( $event )"
    (reorder)="drop( $event )"
    (reverse)="reverseColors()"
    (addColor)="addColor()"
    (removeColor)="removeColor( $event )"
    (generate)="onGenerate( $event )"
    (uploadImage)="handleImageUpload( $event )">
  </app-palette-widget>
</div>

<!-- a chip per section under the hero, one line that slides sideways and stays under the nav (owner 2026-10-12) -->
<nav class="cp-chipbar" appStuck *ngIf="palette.length > 0" aria-label="Palette tool sections">
  <div class="cp-chipbar-row">
    <a *ngFor="let s of pageSections" class="pl-chip" [class.on]="activeSection === s.key" [attr.href]="'#sec-' + s.key"
       (click)="$event.preventDefault(); scrollToSection( s.key )">
      <span>{{ s.label }}</span>
    </a>
  </div>
</nav>

<!-- ===================== Body: every section as a row of two cards (owner 2026-10-12) ===================== -->
<div class="cp-body cp-boxes" *ngIf="palette.length > 0">

  <div class="cp-content">

    <!-- Preview: five full interface blocks stacked on the left, each beside its
         own Prism highlighted CSS on the right, ranked best to worst by WCAG. -->
    <section class="cp-section" id="sec-preview" data-section="preview" *ngIf="previewBlocks.length">
      <header class="cp-head">
      <span class="cp-eyebrow"><span class="cp-eyebrow-swatch" [style.background]="paletteGradient()"></span>{{ sourceLabel }}</span>
      <h2 class="cp-h2">Preview</h2>
      <p class="cp-section-lead">An interface built from your palette's first two colors as surface and card, with its WCAG and APCA readability scores. It does not use every color in the palette.</p>
      </header>
      <div class="cp-main">

      <div class="cp-pv">
        <ng-container *ngIf="previewBlocks[ activePreview ] as b">
          <div class="cp-window cp-pv-ui">
            <div class="cp-window-bar">
              <span class="cp-pv-scores">
                <span class="cp-pv-score" [attr.data-status]="b.wcagStatus">WCAG {{ b.wcagLvl }} &middot; {{ b.ratio }}:1</span>
                <span class="cp-pv-score" [attr.data-status]="b.apcaStatus">APCA Lc {{ b.apca }}</span>
              </span>
              <span class="cp-pv-nav" *ngIf="previewBlocks.length > 1">
                <button type="button" class="cp-pv-prev cp-std-btn" (click)="prevPreview()"
                        [disabled]="activePreview === 0">
                  Prev
                </button>
                <button type="button" class="cp-pv-next cp-std-btn" (click)="shufflePreview()"
                        [disabled]="activePreview >= previewBlocks.length - 1">
                  Next
                </button>
              </span>
            </div>
            <div class="cp-blk" [style.background]="b.surface">
              <div class="cp-blk-text" (mouseenter)="hoveredRule = 'text'" (mouseleave)="hoveredRule = null">
                <span class="cp-blk-text-title" [style.color]="b.text">Text Sample</span>
                <span class="cp-blk-text-body" [style.color]="b.text">This is body text on the page surface. It shows how a paragraph reads in this color pairing, across a couple of lines, so you can judge the contrast before you commit to it.</span>
              </div>

              <div class="cp-blk-card" [style.background]="b.card"
                   (mouseenter)="hoveredRule = 'card'" (mouseleave)="hoveredRule = null">
                <span class="cp-blk-card-title" [style.color]="b.text">Card sample</span>
                <span class="cp-blk-card-text" [style.color]="b.text">A card sits on the surface with its own heading and a line of supporting text.</span>
              </div>

              <div class="cp-blk-banner" [style.background]="b.card" [style.border-left-color]="b.accent3"
                   (mouseenter)="hoveredRule = 'banner'" (mouseleave)="hoveredRule = null">
                <span [style.color]="b.text">Banner sample</span>
              </div>

              <div class="cp-blk-field" (mouseenter)="hoveredRule = 'input'" (mouseleave)="hoveredRule = null">
                <span class="cp-blk-field-label" [style.color]="b.text">Input sample</span>
                <span class="cp-blk-input" [style.background]="b.card"
                      [style.color]="b.text" [style.border-color]="b.accent3">Type here...</span>
              </div>

              <div class="cp-blk-controls">
                <div class="cp-blk-setting" (mouseenter)="hoveredRule = 'toggle'" (mouseleave)="hoveredRule = null">
                  <span class="cp-blk-switch" [style.background]="b.accent2"><span class="cp-blk-knob" [style.background]="b.accentText"></span></span>
                  <span [style.color]="b.text">Switch sample</span>
                </div>
                <div class="cp-blk-setting" (mouseenter)="hoveredRule = 'toggle'" (mouseleave)="hoveredRule = null">
                  <span class="cp-blk-check" [style.background]="b.accent2" [style.color]="b.accentText">
                    <svg class="cp-blk-check-icon" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                      <path d="M5 12.5 10 17.5 19 7" stroke="currentColor" stroke-width="2.6" stroke-linecap="round" stroke-linejoin="round"/>
                    </svg>
                  </span>
                  <span [style.color]="b.text">Checkbox sample</span>
                </div>
              </div>

              <div class="cp-blk-btns">
                <span class="cp-blk-btn" [style.background]="b.accent" [style.color]="b.accentText"
                      (mouseenter)="hoveredRule = 'btn'" (mouseleave)="hoveredRule = null">Continue</span>
                <span class="cp-blk-btn cp-blk-btn-outline" [style.color]="b.text" [style.border-color]="b.accent"
                      (mouseenter)="hoveredRule = 'btn-outline'" (mouseleave)="hoveredRule = null">Learn more</span>
              </div>
            </div>
          </div>

          <div class="cp-window cp-pv-code">
            <div class="cp-window-bar">
              <span class="cp-window-dots"><i></i><i></i><i></i></span>
              <span class="cp-window-file">palette.css</span>
              <button type="button" class="cp-pv-copy cp-std-btn cp-std-btn-copy" (click)="copyText( b.cssText, 'pvcopy' )">
                <app-icon name="copy" aria-hidden="true"></app-icon>{{ copiedKey === 'pvcopy' ? 'Copied' : 'Copy' }}
              </button>
            </div>
            <pre class="cp-window-code"
                 [style.--cp-editor-bg]="editorBg || null"
                 [style.--cp-editor-fg]="editorFg || null"
                 [style.--cp-syntax-comment]="synComment || null"
                 [style.--cp-syntax-selector]="synSelector || null"
                 [style.--cp-syntax-property]="synProperty || null"
                 [style.--cp-syntax-value]="synValue || null"
                 [style.--cp-syntax-keyword]="synKeyword || null"
                 [style.--cp-syntax-punctuation]="synPunctuation || null"><code class="cp-pv-codeblock"><span class="cp-css-rule" *ngFor="let r of b.rules" [class.cp-css-active]="hoveredRule === r.key" [innerHTML]="r.html"></span></code></pre>
          </div>
        </ng-container>
      </div>
      </div>
    </section>

    <!-- Color Channels: the palette's overall color balance (aggregate). -->
    <section class="cp-section" id="sec-channels" data-section="channels" *ngIf="channelStats">
      <header class="cp-head">
      <span class="cp-eyebrow"><span class="cp-eyebrow-swatch" [style.background]="paletteGradient()"></span>{{ sourceLabel }}</span>
      <h2 class="cp-h2">Color Channels</h2>
      <p class="cp-section-lead">Averaged across the whole palette. RGB is each channel's share of the total, plus the average HSL, HSV and CMYK</p>
      </header>
      <div class="cp-main">

      <div class="cp-channel-groups">
        <div class="cp-channel-group">
          <h3 class="cp-h3">RGB</h3>
          <div class="cp-channels">
            <div class="cp-channel" *ngFor="let c of channelStats.rgb">
              <span class="cp-channel-label">{{ c.label }}</span>
              <span class="cp-channel-track">
                <span class="cp-channel-fill" [style.width.%]="c.width" [style.background]="c.color"></span>
              </span>
              <span class="cp-channel-val">{{ c.text }}</span>
            </div>
          </div>
        </div>

        <div class="cp-channel-group">
          <h3 class="cp-h3">CMYK</h3>
          <div class="cp-channels">
            <div class="cp-channel" *ngFor="let c of channelStats.cmyk">
              <span class="cp-channel-label">{{ c.label }}</span>
              <span class="cp-channel-track">
                <span class="cp-channel-fill" [style.width.%]="c.width" [style.background]="c.color"></span>
              </span>
              <span class="cp-channel-val">{{ c.text }}</span>
            </div>
          </div>
        </div>

        <div class="cp-channel-group">
          <h3 class="cp-h3">HSL</h3>
          <div class="cp-channels">
            <div class="cp-channel" *ngFor="let c of channelStats.hsl">
              <span class="cp-channel-label">{{ c.label }}</span>
              <span class="cp-channel-track">
                <span class="cp-channel-fill" [style.width.%]="c.width" [style.background]="c.color"></span>
              </span>
              <span class="cp-channel-val">{{ c.text }}</span>
            </div>
          </div>
        </div>

        <div class="cp-channel-group">
          <h3 class="cp-h3">HSV</h3>
          <div class="cp-channels">
            <div class="cp-channel" *ngFor="let c of channelStats.hsv">
              <span class="cp-channel-label">{{ c.label }}</span>
              <span class="cp-channel-track">
                <span class="cp-channel-fill" [style.width.%]="c.width" [style.background]="c.color"></span>
              </span>
              <span class="cp-channel-val">{{ c.text }}</span>
            </div>
          </div>
        </div>
      </div>
      </div>
    </section>

    <!-- Color conversions: one row per colour space, each listing the whole
         palette on a single line, copyable in one click. Two columns of colour
         spaces (owner 2026-10-12), one on phones. -->
    <section class="cp-section" id="sec-conversions" data-section="conversions" *ngIf="paletteConversionGroups.length">
      <header class="cp-head">
      <span class="cp-eyebrow"><span class="cp-eyebrow-swatch" [style.background]="paletteGradient()"></span>{{ sourceLabel }}</span>
      <h2 class="cp-h2">Color Conversions</h2>
      <p class="cp-section-lead">Every palette color across each color space. Copy a whole row in one click</p>
      </header>
      <div class="cp-main">

      <div class="cp-pconv-grid">
      <table class="cp-pconv-table" *ngFor="let col of paletteConversionCols">
        <tbody>
          <ng-container *ngFor="let g of col">
            <tr class="cp-pconv-grouprow"><td colspan="3">{{ g.title }}</td></tr>
            <tr *ngFor="let row of g.rows">
              <td class="cp-pconv-label">{{ row.label }}</td>
              <td class="cp-pconv-value" [title]="row.value">{{ row.value }}</td>
              <td class="cp-pconv-copy">
                <button type="button" class="cp-std-btn cp-std-btn-copy" (click)="copyText( row.value, row.key )">
                  <app-icon name="copy" aria-hidden="true"></app-icon>{{ copiedKey === row.key ? 'Copied' : 'Copy' }}
                </button>
              </td>
            </tr>
          </ng-container>
        </tbody>
      </table>
      </div>
      </div>
    </section>

    <!-- Harmony: a harmonic score per colour scheme (same schemes + order as the
         color tool), plus the palette's warm/cool balance. -->
    <section class="cp-section" id="sec-harmony" data-section="harmony" *ngIf="harmonyResult.length > 0">
      <header class="cp-head">
      <span class="cp-eyebrow"><span class="cp-eyebrow-swatch" [style.background]="paletteGradient()"></span>{{ sourceLabel }}</span>
      <h2 class="cp-h2">Harmony</h2>
      <p class="cp-section-lead">A harmonic score per scheme: the share of your palette whose colors form that relationship on the wheel. Click any lit swatch to open it in the Color Explorer</p>
      </header>
      <div class="cp-main">

      <div class="cp-scheme" *ngFor="let h of harmonyScores; index as hi">
        <h3 class="cp-h3 cp-harmony-head" [class.cp-h3-gap]="hi > 0" [class.cp-harmony-zero]="h.score === 0">
          <span class="cp-harmony-name">{{ h.name }}:</span>
          <span class="cp-harmony-dot" [attr.data-level]="harmonyLevel( h.score, palette.length )"></span>
          <span class="cp-harmony-score">{{ h.score }}</span>
        </h3>
        <p class="cp-scheme-desc">{{ h.description }}</p>
        <div class="cp-swatch-row">
          <div class="cp-swatch" *ngFor="let color of palette; index as i">
            <button type="button" class="cp-swatch-color" [class.cp-swatch-muted]="h.counts[i] <= 0"
                    [style.background-color]="color.value"
                    (click)="h.counts[i] > 0 ? openRouteInNewTab(URLS.tools.color + '/' + color.value.replace('#', '')) : null"
                    [attr.aria-label]="color.value"></button>
            <span class="cp-swatch-hex">{{ h.counts[i] }}</span>
          </div>
        </div>
      </div>

      <div class="cp-scheme">
        <h3 class="cp-h3 cp-harmony-head cp-h3-gap">
          <span class="cp-harmony-name">Temperature:</span>
          <span class="cp-harmony-dot" data-level="high"></span>
          <span class="cp-harmony-score">{{ harmonyResult[ 3 ].decision | titlecase }}</span>
        </h3>
        <p class="cp-scheme-desc">Warm hues against cool ones. Their balance sets the overall mood.</p>
        <div class="cp-swatch-row">
          <div class="cp-swatch" *ngFor="let color of palette; index as i">
            <button type="button" class="cp-swatch-color"
                    [class.cp-swatch-muted]="harmonyResult[3].decision !== 'equal' && temperatures[i] !== harmonyResult[3].decision"
                    [style.background-color]="color.value"
                    (click)="openRouteInNewTab(URLS.tools.color + '/' + color.value.replace('#', ''))"
                    [attr.aria-label]="color.value"></button>
            <span class="cp-swatch-hex cp-temp-label"
                  [class.cp-temp-minor]="harmonyResult[3].decision !== 'equal' && temperatures[i] !== harmonyResult[3].decision">{{ temperatures[ i ] }}</span>
          </div>
        </div>
      </div>
      </div>
    </section>

    <!-- Shades, Tints & Tones: a short ramp of every palette colour toward
         white / black / gray under each sub-heading. -->
    <section class="cp-section" id="sec-shades" data-section="shades" *ngIf="shadeTintTone.length">
      <header class="cp-head">
      <span class="cp-eyebrow"><span class="cp-eyebrow-swatch" [style.background]="paletteGradient()"></span>{{ sourceLabel }}</span>
      <h2 class="cp-h2">Tints, Shades &amp; Tones</h2>
      <p class="cp-section-lead">A shade adds black, a tint adds white, a tone adds gray. The top row is your palette, each row below steps it one notch further</p>
      </header>
      <div class="cp-main">

      <div class="cp-scheme" *ngFor="let r of shadeTintTone; index as ri">
        <h3 class="cp-h3" [class.cp-h3-gap]="ri > 0">{{ r.title }}</h3>
        <div class="cp-swatch-row" *ngFor="let row of r.rows; index as rowi">
          <div class="cp-swatch" *ngFor="let color of row; index as ci">
            <button type="button" class="cp-swatch-color" [style.background-color]="color"
                    (click)="copyText( color, r.title + '-' + rowi + '-' + ci )" [attr.aria-label]="'Copy ' + color"></button>
            <span class="cp-swatch-hex">{{ copiedKey === r.title + '-' + rowi + '-' + ci ? 'Copied' : color }}</span>
          </div>
        </div>
      </div>
      </div>
    </section>

    <!-- Temperature: the whole palette nudged once warmer and once cooler. -->
    <section class="cp-section" id="sec-temperature" data-section="temperature" *ngIf="temperatureVariants.length">
      <header class="cp-head">
      <span class="cp-eyebrow"><span class="cp-eyebrow-swatch" [style.background]="paletteGradient()"></span>{{ sourceLabel }}</span>
      <h2 class="cp-h2">Temperature</h2>
      <p class="cp-section-lead">Your palette along the warm to cool axis. The top row is your palette, each row below steps it a little warmer or cooler</p>
      </header>
      <div class="cp-main">

      <div class="cp-scheme" *ngFor="let r of temperatureVariants; index as ti">
        <h3 class="cp-h3" [class.cp-h3-gap]="ti > 0">{{ r.title }}</h3>
        <div class="cp-swatch-row" *ngFor="let row of r.rows; index as rowi">
          <div class="cp-swatch" *ngFor="let color of row; index as ci">
            <button type="button" class="cp-swatch-color" [style.background-color]="color"
                    (click)="copyText( color, r.title + '-temp-' + rowi + '-' + ci )" [attr.aria-label]="'Copy ' + color"></button>
            <span class="cp-swatch-hex">{{ copiedKey === r.title + '-temp-' + rowi + '-' + ci ? 'Copied' : color }}</span>
          </div>
        </div>
      </div>
      </div>
    </section>

    <!-- Color vision: how the palette reads under common deficiencies. -->
    <section class="cp-section" id="sec-vision" data-section="vision" *ngIf="palette.length > 0">
      <header class="cp-head">
      <span class="cp-eyebrow"><span class="cp-eyebrow-swatch" [style.background]="paletteGradient()"></span>{{ sourceLabel }}</span>
      <h2 class="cp-h2">Color Vision Deficiency</h2>
      <p class="cp-section-lead">How this palette reads to people with color vision deficiencies</p>
      </header>
      <div class="cp-main">

      <!-- The original palette once at the top, then each cone family below it. -->
      <div class="cp-cv-block cp-cv-original">
        <p class="cp-cv-cap"><span class="cp-cv-name">Original</span></p>
        <div class="cp-swatch-row">
          <div class="cp-swatch" *ngFor="let color of palette">
            <button type="button" class="cp-swatch-color" [style.background-color]="color.value"
                    (click)="openRouteInNewTab(URLS.tools.color + '/' + color.value.replace('#', ''))"
                    [attr.aria-label]="color.value"></button>
            <span class="cp-swatch-hex">{{ color.value }}</span>
          </div>
        </div>
      </div>

      <div class="cp-cv-group" *ngFor="let group of colorVisionGroups()">
        <h3 class="cp-h3">{{ group.group }}</h3>

        <div class="cp-cv-block" *ngFor="let item of group.items">
          <p class="cp-cv-cap"><span class="cp-cv-name">{{ item.name }}:</span> <span class="cp-cv-desc">{{ item.note }}</span></p>
          <div class="cp-swatch-row">
            <div class="cp-swatch" *ngFor="let color of item.colors">
              <button type="button" class="cp-swatch-color" [style.background-color]="color"
                      (click)="openRouteInNewTab(URLS.tools.color + '/' + color.replace('#', ''))"
                      [attr.aria-label]="color"></button>
              <span class="cp-swatch-hex">{{ color }}</span>
            </div>
          </div>
        </div>
      </div>
      </div>
    </section>

    <!-- More Color Tools (owner 2026-10-22): the Color Picker, above Related Themes -->
    <app-more-tools sectionKey="more" title="More Color Tools" lead="Pick any color on your screen and read it the same way, right from your menu bar"
                    [app]="colorPicker" [source]="moreToolsSource"></app-more-tools>

    <!-- Related Themes: IDE themes whose palette sits closest to this one. -->
    <section class="cp-section" id="sec-themes" data-section="themes" *ngIf="relatedThemeList.length">
      <header class="cp-head">
      <h2 class="cp-h2">Related Themes</h2>
      <p class="cp-section-lead">IDE themes whose palette sits closest to this one</p>
      </header>
      <div class="cp-main">

      <div class="cp-theme-grid">
        <app-theme-card *ngFor="let t of relatedThemeList"
                        [product]="t.product"
                        [category]="t.category"
                        [compact]="false"
                        [tile]="false"
                        [selectable]="false"
                        [clickable]="true"
                        [screenshotMode]="false">
        </app-theme-card>
      </div>
      </div>
    </section>

    <!-- From The Blog: blog poster cards, same design as the blog page hero. -->
    <section class="cp-section" id="sec-blog" data-section="blog">
      <header class="cp-head">
      <h2 class="cp-h2">From The Blog</h2>
      <p class="cp-section-lead">Practical reads on how colors are built and combined</p>
      </header>
      <div class="cp-main">

      <div class="cp-blog-cards">
        <a class="cp-blog-card" *ngFor="let post of blogCards" [routerLink]="post.url"
           [style.--card-accent]="post.coverAccent || null">
          <img class="cp-blog-bg" [src]="post.cover" [alt]="post.title" loading="lazy" decoding="async">
          <span class="cp-blog-scrim"></span>
          <svg class="cp-blog-part" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 210 297" aria-hidden="true">
            <path d="M 31.496131,83.498219 C 17.281586,96.049685 13.920954,122.9803 24.61827,138.63631 v 0 c 8.67477,16.8641 33.95747,26.73174 51.762024,20.2132 v 0 l 54.361716,54.36558 -83.532627,83.53647 60.446297,8e-5 84.02015,-83.53655 -84.82866,-84.83262 C 113.3695,110.57782 103.49808,85.295107 86.634028,76.620298 v 0 C 80.390777,72.357466 72.362085,70.323536 64.161412,70.323536 v 0 c -12.368001,0 -25.118271,4.626039 -32.665281,13.174683"/>
          </svg>
          <span class="cp-blog-content">
            <span class="cp-blog-cat">{{ post.categoryLabel }}</span>
            <span class="cp-blog-title">{{ post.title }}</span>
            <span class="cp-blog-meta">{{ post.dateLabel }} &middot; {{ post.readingTimeMinutes }} min read</span>
          </span>
        </a>
      </div>
      </div>
    </section>


    <!-- How It Works + FAQ closes the page, after the blog: the same closer every tool has -->
    <app-tool-about [lead]="about.lead" [paragraphs]="about.paragraphs" [faq]="about.faq" ratingKey="palette" ratingName="the Palette Generator"></app-tool-about>

  </div>
</div>
